import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { useSettingsDialog } from '@core/hooks/useSettingsDialog';
import { Container } from '@soteria/design-system-ve';
import { useState } from 'react';

import { Guide } from './Guide';
import * as styles from './NavigationHeader.css';
import { NavigationSlideout } from './NavigationSlideout';
import { NotificationBell } from './NotificationBell';
import { SettingsDialog } from './SettingsDialog';
import { UserProfileButton } from './UserProfile';

interface NavigationHeaderProps {
  userName?: string;
  onLogout: () => void;
}

/**
 * Hamburger menu icon component
 */
function HamburgerIcon() {
  return (
    <svg className={styles.hamburgerIcon} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
    </svg>
  );
}

export function NavigationHeader({ userName, onLogout }: NavigationHeaderProps) {
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);

  // Use our custom hook to manage the settings dialog
  const { isOpen, closeDialog } = useSettingsDialog();

  // Check if notifications feature is enabled
  const isNotificationFeatureEnabled = useIsFeatureEnabled(FEATURE_FLAGS.NOTIFICATIONS);

  // Check if Guide feature is enabled
  const isGuideEnabled = useIsFeatureEnabled(FEATURE_FLAGS.GUIDE);

  // Handle slideout toggle
  const handleSlideoutToggle = () => {
    setIsSlideoutOpen(!isSlideoutOpen);
  };

  const handleSlideoutClose = () => {
    setIsSlideoutOpen(false);
  };

  return (
    <Container padding="none" size="xxl">
      <div className={styles.header}>
        {/* Left side - Hamburger button */}
        <button aria-label="Open navigation menu" className={styles.hamburgerButton} onClick={handleSlideoutToggle}>
          <HamburgerIcon />
        </button>

        {/* Right side - Controls */}
        <div className={styles.controls}>
          {isNotificationFeatureEnabled && <NotificationBell count={3} />}
          <UserProfileButton
            user={{
              name: userName || 'User',
              avatar: undefined,
            }}
            onSignOut={onLogout}
          />
        </div>
      </div>

      {/* Navigation slideout - conditionally render Guide or NavigationSlideout based on feature flag */}
      {isGuideEnabled ? (
        <Guide isOpen={isSlideoutOpen} onClose={handleSlideoutClose} />
      ) : (
        <NavigationSlideout isOpen={isSlideoutOpen} onClose={handleSlideoutClose} />
      )}

      {/* Settings Dialog - Now controlled by Redux */}
      <SettingsDialog isOpen={isOpen} onClose={closeDialog} />
    </Container>
  );
}
